import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import CheckIcon from '@mui/icons-material/Check';
import { captureError, formatErrorReport, buildMailtoUrl, type CapturedError } from '../utils/errorReporting';

interface ErrorReportDialogProps {
  open: boolean;
  error: unknown;
  context?: Record<string, unknown>;
  onClose: () => void;
}

export const ErrorReportDialog: React.FC<ErrorReportDialogProps> = ({ open, error, context, onClose }) => {
  const [userNote, setUserNote] = useState('');
  const [copied, setCopied] = useState(false);

  const captured: CapturedError = React.useMemo(
    () => captureError(error, context),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open]
  );

  const handleCopy = async () => {
    const report = formatErrorReport(captured, userNote);
    try {
      await navigator.clipboard.writeText(report);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // fallback for browsers that block clipboard
      const el = document.createElement('textarea');
      el.value = report;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleClose = () => {
    setUserNote('');
    setCopied(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send Error Report</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Something went wrong. You can send us a report to help diagnose the issue.
        </Typography>

        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
          {captured.message}
          {captured.code && <Box component="span" sx={{ color: 'text.secondary' }}>{` (${captured.code})`}</Box>}
        </Box>

        <TextField
          fullWidth
          multiline
          rows={3}
          label="What were you doing? (optional)"
          placeholder="e.g. I was trying to sign up with my Google account..."
          value={userNote}
          onChange={(e) => setUserNote(e.target.value)}
          inputProps={{ maxLength: 500 }}
          sx={{ mt: 2 }}
          size="small"
        />

        {copied && (
          <Alert severity="success" icon={<CheckIcon fontSize="small" />} sx={{ mt: 1.5 }}>
            Report copied to clipboard!
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={handleClose} color="inherit" size="small">
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={() => void handleCopy()}
          startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
          variant="outlined"
          size="small"
        >
          {copied ? 'Copied!' : 'Copy report'}
        </Button>
        <Button
          href={buildMailtoUrl(captured, userNote)}
          component="a"
          startIcon={<EmailIcon />}
          variant="contained"
          size="small"
        >
          Email us
        </Button>
      </DialogActions>
    </Dialog>
  );
};
